import { Component, ElementRef, ViewEncapsulation } from '@angular/core';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { removeAmChartLogo } from 'app/core/shared/amchart4/amchart4.module';
import { Router } from '@angular/router';
import { AxisItemLocation } from '@amcharts/amcharts4/.internal/charts/axes/Axis';
import { CmDashboardService } from 'app/modules/corrective-maintenance/services/cm-dashboard.service';
import module from 'app/modules/corrective-maintenance/corrective-maintenance.module';

am4core.useTheme(am4themes_animated);

@Component({
    selector: 'app-cm-dashboard',
    templateUrl: './cm-dashboard.page.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: module,
    styleUrls: ['./cm-dashboard.page.scss'],
})
export class CMDashboardPage {
    fieldSummary$ = this.cmDashboardService.fieldSummary$;

    currentYear: number = new Date().getFullYear();
    cmStatus: any;
    failureEquipments: any;
    remedialProgress: any;

    constructor(
        private elementRef: ElementRef,
        public router: Router,
        private cmDashboardService: CmDashboardService,
    ) { }

    ngOnInit(): void {
        this.cmDashboardService
            .getCmFieldSummary({ page: 1, pageSize: 10 })
            .subscribe();

        this.fieldSummary$.subscribe((fieldSummary) => {
            this.cmStatus = fieldSummary?.data.cmStatus;
            this.failureEquipments = fieldSummary?.data.failureEquipments;
            this.remedialProgress = fieldSummary?.data.remidiaProgress;

            if (this.cmStatus) {
                this.createPieChart();
            }

            if (this.failureEquipments) {
                this.createBarGraph();
            }

            if (this.remedialProgress) {
                console.log('remedialProgress', this.remedialProgress);
                this.remedialProgressGraph();
            }
        });
    }

    private createPieChart() {
        // Create chart instance
        let chart = am4core.create(
            'cmCompletenesspieChart',
            am4charts.PieChart,
        );
        chart.padding(15, 10, 5, 0);

        // Add data
        chart.data = [
            {
                category: 'Completed',
                value: this.cmStatus.close,
                color: am4core.color('#7ed77e'),
            },
            {
                category: 'Pending',
                value: this.cmStatus.open,
                color: am4core.color('#FFC300'),
            },
        ];

        // Add and configure Series
        let pieSeries = chart.series.push(new am4charts.PieSeries());
        pieSeries.dataFields.value = 'value';
        pieSeries.dataFields.category = 'category';
        pieSeries.slices.template.stroke = am4core.color('#fff');
        pieSeries.slices.template.strokeWidth = 2;
        pieSeries.slices.template.strokeOpacity = 1;
        pieSeries.slices.template.propertyFields.fill = 'color'; // You can specify a default color
        pieSeries.labels.template.fill = am4core.color('#000');

        pieSeries.labels.template.text =
            "{values.value.percent.formatNumber('#.#')}%";
        pieSeries.alignLabels = false;
        pieSeries.labels.template.padding(0, 0, 0, 0);
        pieSeries.ticks.template.disabled = true;

        // This creates initial animation
        pieSeries.hiddenState.properties.opacity = 1;
        pieSeries.hiddenState.properties.endAngle = -90;
        pieSeries.hiddenState.properties.startAngle = -90;

        // Add a legend
        chart.legend = new am4charts.Legend();
        chart.legend.position = 'bottom';
        chart.legend.valign = 'bottom';
        chart.legend.contentAlign = 'center';
        chart.legend.labels.template.fontSize = 12;
        chart.legend.labels.template.fill = am4core.color('#000');
        chart.legend.itemContainers.template.paddingTop = 0;
        chart.legend.itemContainers.template.paddingBottom = 0;
        chart.legend.marginTop = 20;
        chart.legend.valueLabels.template.disabled = true;

        // Custom marker style
        chart.legend.markers.template.width = 12;
        chart.legend.markers.template.height = 12;
        let markerTemplate = chart.legend.markers.template.children.getIndex(
            0,
        ) as am4core.RoundedRectangle;
        markerTemplate.cornerRadius(4, 4, 4, 4);

        removeAmChartLogo(this.elementRef.nativeElement);
    }

    private createBarGraph() {
        // Create chart instance
        let chart = am4core.create('failEquipCatBarGraph', am4charts.XYChart);
        console.log('failureEquipments', this.failureEquipments);
        chart.data = this.failureEquipments;

        // Create axes
        let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = 'categoryShortName';
        categoryAxis.renderer.grid.template.location = 0;
        categoryAxis.renderer.minGridDistance = 20;

        categoryAxis.renderer.labels.template.disabled = true;

        let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.renderer.labels.template.fill = am4core.color('#000');

        // Create series
        let series = chart.series.push(new am4charts.ColumnSeries());
        series.dataFields.valueY = 'count';
        series.dataFields.categoryX = 'categoryShortName';
        series.name = 'Counts';
        series.columns.template.tooltipText = '{categoryX}: [bold]{valueY}[/]';
        series.columns.template.fillOpacity = 0.8;

        let columnTemplate = series.columns.template;
        columnTemplate.strokeWidth = 0;
        columnTemplate.strokeOpacity = 0;

        // Rainbow palette starting from Blue: Blue → Cyan → Green → Yellow → Orange → Red → Pink → Purple
        const distinctColors = [
            '#3A86FF', // bright blue
            '#4361EE', // indigo blue
            '#00BBF9', // sky blue
            '#4CC9F0', // bright cyan
            '#00F5D4', // cyan
            '#2EC4B6', // teal
            '#85d6a1ff', // light green
            '#2ECC71', // green
            '#248546ff', // light green
            '#1ad185ff', // light green
            '#FFBE0B', // yellow
            '#a57b09ff', // yellow
            '#F77F00', // orange
            '#f1a762ff', // orange
            '#f01023ff', // red
            '#eb3847ff', // red
            '#bf3f49ff', // red
            '#750a13ff', // red
        ];

        // Assign each bar a color from the distinct palette
        columnTemplate.adapter.add('fill', function (fill, target) {
            const idx = target.dataItem ? target.dataItem.index % distinctColors.length : 0;
            return am4core.color(distinctColors[idx]);
        });

        // Build a custom 2-row horizontal scrolling legend in HTML after the chart renders
        chart.events.on('ready', function (event) {
            const legendContainer = document.getElementById('failEquipLegend');
            if (!legendContainer) return;

            // Collect all items: { name, color }
            const items: { name: string; color: string }[] = [];
            series.columns.each(function (column) {
                items.push({
                    name: (column.dataItem as any).categoryX,
                    color: (column.fill as am4core.Color).hex,
                });
            });

            // Split into 3 rows
            const third = Math.ceil(items.length / 3);
            const row1 = items.slice(0, third);
            const row2 = items.slice(third, third * 2);
            const row3 = items.slice(third * 2);

            // Build HTML
            const itemWidth = '130px';
            let html = `<div style="display:inline-flex; flex-direction:column; min-width:100%;">`;

            // Function to render a row
            function buildRow(rowItems) {
                let rowHtml = `<div style="display:flex; flex-direction:row;justify-content:center;">`;
                rowItems.forEach(item => {
                    rowHtml += `<div style="display:flex; align-items:center; min-width:${itemWidth}; padding:3px 8px; white-space:nowrap;">
            <span style="display:inline-block; width:10px; height:10px; border-radius:3px; background:${item.color}; flex-shrink:0; margin-right:5px;"></span>
            <span style="font-size:11px; color:#444; overflow:hidden; text-overflow:ellipsis;" title="${item.name}">${item.name}</span>
        </div>`;
                });
                rowHtml += `</div>`;
                return rowHtml;
            }

            // Row 1
            html += buildRow(row1);

            // Row 2
            html += buildRow(row2);

            // Row 3
            html += buildRow(row3);

            html += `</div>`;

            legendContainer.innerHTML = html;
        });

        removeAmChartLogo(this.elementRef.nativeElement);
    }

    private remedialProgressGraph() {
        // Create chart instance
        var chart = am4core.create('remedialProgressGraph', am4charts.XYChart);

        // Add data
        chart.data = this.remedialProgress;

        // Create axes
        let dateAxis = chart.xAxes.push(new am4charts.DateAxis());
        dateAxis.renderer.grid.template.location = 0;
        dateAxis.renderer.minGridDistance = 20;
        dateAxis.dateFormats.setKey('month', 'MMM');
        dateAxis.periodChangeDateFormats.setKey('month', 'MMM');
        dateAxis.renderer.labels.template.paddingTop = 40; // You can adjust the value as needed
        dateAxis.renderer.labels.template.fontWeight = 'bolder'; // You can adjust the value as needed
        dateAxis.renderer.labels.template.fill = am4core.color('#000');

        var valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.min = 0;
        valueAxis.renderer.labels.template.fill = am4core.color('#000');

        // Create series
        function createSeries(field, name, isStacked, color) {
            var series = chart.series.push(new am4charts.ColumnSeries());
            series.dataFields.valueY = field;
            series.dataFields.dateX = 'monthDate';
            series.name = name;
            series.columns.template.tooltipText = '{dateX}: [b]{valueY}[/]';
            series.strokeWidth = 2;
            series.stacked = isStacked;
            series.columns.template.strokeWidth = 0;
            series.fill = am4core.color(color);
        }

        // Baseline group: Blue family (bright → deep)
        createSeries('baseline', 'Baseline', false, '#3A86FF');
        createSeries(
            'cumulativeBaselineRect',
            'Cum B. Rectified',
            true,
            '#0043CE',
        );

        // Emerging group: Teal/Green family (vivid → deep)
        createSeries(
            'remainingEmergingCarryForward',
            'Remaining Emerging C/F',
            false,
            '#2ECC71',
        );
        createSeries(
            'cumulativeEmergingRect',
            'Cum E. Rectified',
            true,
            '#248546ff',
        );

        // General group: Amber/Orange family (warm → deep)
        createSeries('remainingGeneral', 'General', false, '#FFB703');
        createSeries(
            'cumulativeGeneralRect',
            'Cum G. Rectified',
            true,
            '#E76F51',
        );

        // Create ranges
        function createRange(from, to, label) {
            let range = dateAxis.axisRanges.create();
            range.date = from;
            range.endDate = to;
            // range.tick.disabled = false;
            // range.tick.strokeOpacity = 0.3;
            // range.tick.length = 60;

            range.label.text = label;
            range.label.paddingTop = 0;
            range.label.rotation = -90;
            range.label.fontSize = '12px';
            range.label.fontWeight = '400';
            range.label.location = 0.2 as AxisItemLocation;
            range.label.horizontalCenter = 'middle';

            range.grid.disabled = true;
        }

        // for 12 months - divide each month into 3 equal parts
        for (let i = 0; i < 12; i++) {
            // Get the last day of the current month
            const lastDayOfMonth = new Date(
                this.currentYear,
                i + 1,
                0,
            ).getDate();
            const thirdOfMonth = Math.floor(lastDayOfMonth / 3);

            // Baseline: first third
            createRange(
                new Date(this.currentYear, i, 1),
                new Date(this.currentYear, i, thirdOfMonth),
                'Baseline',
            );

            // Emerging: second third
            createRange(
                new Date(this.currentYear, i, thirdOfMonth + 1),
                new Date(this.currentYear, i, thirdOfMonth * 2),
                'Emerging',
            );

            // General: last third
            createRange(
                new Date(this.currentYear, i, thirdOfMonth * 2 + 1),
                new Date(this.currentYear, i, lastDayOfMonth),
                'General',
            );
        }

        function createRangeGrid(date) {
            var range = dateAxis.axisRanges.create();
            range.date = date;
            range.grid.strokeOpacity = 0.2;
            range.tick.disabled = false;
            range.tick.strokeOpacity = 0.3;
            range.tick.length = 60;
            // range.label.text = "M"
        }

        createRangeGrid(new Date(this.currentYear, 0, 1));
        createRangeGrid(new Date(this.currentYear, 1, 1));
        createRangeGrid(new Date(this.currentYear, 2, 1));
        createRangeGrid(new Date(this.currentYear, 3, 1));
        createRangeGrid(new Date(this.currentYear, 4, 1));
        createRangeGrid(new Date(this.currentYear, 5, 1));
        createRangeGrid(new Date(this.currentYear, 6, 1));
        createRangeGrid(new Date(this.currentYear, 7, 1));
        createRangeGrid(new Date(this.currentYear, 8, 1));
        createRangeGrid(new Date(this.currentYear, 9, 1));
        createRangeGrid(new Date(this.currentYear, 10, 1));
        createRangeGrid(new Date(this.currentYear, 11, 1));
        createRangeGrid(new Date(this.currentYear + 1, 0, 1));

        chart.legend = new am4charts.Legend();
        chart.legend.position = 'bottom';
        chart.legend.valign = 'bottom';
        chart.legend.contentAlign = 'center';
        chart.legend.labels.template.fontSize = 12;
        chart.legend.labels.template.fill = am4core.color('#000');
        chart.legend.itemContainers.template.paddingTop = 0;
        chart.legend.itemContainers.template.paddingBottom = 0;
        chart.legend.marginTop = 20;

        // Custom marker style
        chart.legend.markers.template.width = 12;
        chart.legend.markers.template.height = 12;
        let markerTemplate = chart.legend.markers.template.children.getIndex(
            0,
        ) as am4core.RoundedRectangle;
        markerTemplate.cornerRadius(4, 4, 4, 4);

        removeAmChartLogo(this.elementRef.nativeElement);
    }

    toggleSummary(routes: any) {
        this.router.navigate([`/wim/cm-dashboard/${routes}`]);
    }
}
